import { Link } from 'react-router-dom';
import type { ContractSub } from '@/lib/apiClient';
import { fmtDate } from '@/lib/dates';
import {
  formChips, contextDate, jobHref, TYPE_LABELS, TYPE_NAMES, DATE_COLUMN_LABEL,
  type SubSection, type FormChip,
} from './contractsModel';

/** Which headings get a colored edge. The words carry the meaning; the color only echoes it. */
const SECTION_TONE: Partial<Record<ContractSub['bucket'], 'warn' | 'ok' | 'muted'>> = {
  missing_items: 'warn',
  cleared: 'ok',
  voided: 'muted',
};

/** A form not yet due carries no glyph: neutral grey and the words for a screen reader say it. */
const GLYPH = { received: ' ✓', missing: ' !', waiting: '' } as const;

/** One form on the sheet's CC / NV / W9 / COI strip: the code, a glyph, and words for anyone who can't see color. */
function Chip({ chip }: { chip: FormChip }) {
  const status = chip.state === 'received'
    ? `received ${fmtDate(chip.on)}`
    : chip.state === 'missing' ? 'missing' : 'not received yet';
  const words = `${chip.label}: ${status}`;
  return (
    <span className={`ct-chip ct-chip--${chip.state}`} title={words}>
      <span aria-hidden="true">{chip.short}{GLYPH[chip.state]}</span>
      <span className="ct-sr">{words}</span>
    </span>
  );
}

const dash = <span className="ct-none">—</span>;

/**
 * Subcontracts under the sheet's headings. Shared by the /contracts page (which
 * shows the Job column) and a job's own tab (which does not).
 *
 * Where a subcontract stands — which heading, which forms are missing — was
 * decided by the API and is only displayed here.
 */
export default function SubcontractSections({ sections, canEdit, showJob, onEdit }: {
  sections: SubSection[];
  canEdit: boolean;
  showJob: boolean;
  onEdit: (row: ContractSub) => void;
}) {
  return (
    <>
      {sections.map((s) => {
        const tone = SECTION_TONE[s.bucket];
        const dateLabel = DATE_COLUMN_LABEL[s.bucket];
        const titleId = `ct-section-${s.key.replace(/[^a-z0-9]+/gi, '-')}`;
        return (
          <section key={s.key} className={`ct-section${tone ? ` ct-section--${tone}` : ''}`} aria-labelledby={titleId}>
            <header className="ct-section-head">
              <h2 className="ct-section-title" id={titleId}>{s.title}</h2>
              <span className="ct-section-count">{s.rows.length}</span>
            </header>
            <div className="ew-table-scroll">
              <table className="ct-table ct-table--subs">
                <thead>
                  <tr>
                    <th scope="col" className="ct-w-forms">Forms</th>
                    {showJob && <th scope="col" className="ct-w-job">Job</th>}
                    <th scope="col" className="ct-w-doc">Subcontract</th>
                    <th scope="col">Vendor</th>
                    <th scope="col" className="ct-w-date">Originally sent</th>
                    {/* Always present, so the columns line up from section to section. */}
                    <th scope="col" className="ct-w-date">{dateLabel ?? <span className="ct-sr">Date</span>}</th>
                    <th scope="col">Notes</th>
                    {canEdit && <th scope="col" className="ct-w-act"><span className="ct-sr">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((r) => {
                    const when = contextDate(r);
                    return (
                      <tr key={r.id} className={r.bucket === 'voided' ? 'ct-row--voided' : undefined}>
                        <td>
                          <span className="ct-chips">
                            {formChips(r).map((c) => <Chip key={c.key} chip={c} />)}
                          </span>
                        </td>
                        {showJob && (
                          <td className="ct-job">
                            {r.projectId !== null ? (
                              <>
                                {/* A job whose number has since been blanked is still a job: keep the link. */}
                                <Link to={jobHref(r.projectId)}>{r.jobNumber || 'No job number'}</Link>
                                {r.projectTitle && <span className="ct-under">{r.projectTitle}</span>}
                              </>
                            ) : (
                              // An MSA is not tied to a job (#631). Words, not a dash: a dash reads as missing data.
                              <span className="ct-none" title="An MSA isn’t tied to a job">No job</span>
                            )}
                          </td>
                        )}
                        <td className="ct-date">{r.sageDocNo ?? dash}</td>
                        <td>
                          <span className={r.bucket === 'voided' ? 'ct-strike' : undefined}>{r.vendorName}</span>
                          {r.agreementType !== 'subcontract' && (
                            <span className="ct-tag" title={TYPE_NAMES[r.agreementType]}>{TYPE_LABELS[r.agreementType]}</span>
                          )}
                          {r.newVendor && <span className="ct-tag ct-tag--new">New vendor</span>}
                        </td>
                        <td className="ct-date">{r.originallySentOn ? fmtDate(r.originallySentOn) : dash}</td>
                        <td className="ct-date">{dateLabel ? (when ? fmtDate(when.on) : dash) : null}</td>
                        <td className="ct-notes">{r.notes ?? ''}</td>
                        {canEdit && (
                          <td>
                            <button
                              type="button"
                              className="ew-btn-outline"
                              onClick={() => onEdit(r)}
                              aria-label={`Edit ${r.vendorName}`}
                            >
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </>
  );
}
